import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
  Chip,
  Box,
  CardContent,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Scrollbar from '../scrollbar';

const TABLE_HEAD = [
  { id: 'name', label: 'Name', alignRight: false },
  { id: 'team_lead', label: 'Team Lead', alignRight: false },
  { id: 'supervisor', label: 'Supervisor', alignRight: true },
  { id: 'status', label: 'Status', alignRight: false },
  { id: 'actions', label: 'Actions', alignRight: true },
];

const TeamsTable = ({
  teams,
  isLoading,
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  onViewTeam,
  onEditTeam,
  onDeleteTeam,
}) => {
  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Teams
        </Typography>
        <Scrollbar>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {TABLE_HEAD.map((headCell) => (
                    <TableCell key={headCell.id} align={headCell.alignRight ? 'right' : 'left'}>
                      {headCell.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={5} sx={{ py: 3 }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : teams?.length === 0 ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="textSecondary">
                        {isLoading ? 'Loading teams...' : 'No teams found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {teams.map((team) => (
                    <TableRow hover key={team.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {team.name}
                          </Typography>
                          {team.description && (
                            <Typography variant="caption" color="textSecondary">
                              {team.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{team.team_lead?.full_name || '-'}</TableCell>
                      <TableCell align="right">{team.supervisor?.full_name || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={team.is_active ? 'Active' : 'Inactive'}
                          size="small"
                          color={team.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton size="small" onClick={() => onViewTeam(team)} sx={{ color: 'primary.main' }}>
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => onEditTeam(team)} sx={{ color: 'primary.main' }}>
                            <EditIcon />
                          </IconButton>
                          <IconButton size="small" onClick={() => onDeleteTeam(team)} sx={{ color: 'error.main' }}>
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page - 1} // Material-UI uses 0-based indexing
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </CardContent>
    </Card>
  );
};

TeamsTable.propTypes = {
  teams: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      team_lead: PropTypes.shape({
        full_name: PropTypes.string,
      }),
      supervisor: PropTypes.shape({
        full_name: PropTypes.string,
      }),
      is_active: PropTypes.bool.isRequired,
    })
  ).isRequired,
  isLoading: PropTypes.bool.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  totalCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onViewTeam: PropTypes.func.isRequired,
  onEditTeam: PropTypes.func.isRequired,
  onDeleteTeam: PropTypes.func.isRequired,
};

export default TeamsTable;
